import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useId,
  useRef,
} from 'react';
import { flushSync } from 'react-dom';
import {
  getEffectiveDurationMinutes,
  formatDurationMinutes,
} from '../../../../../../utils/taskDuration';
import { splitMinutesToDHM } from '../utils/splitMinutesToDHM';

function durationUnitActive(value) {
  const n = value === '' ? NaN : parseInt(String(value), 10);
  return Number.isFinite(n) && n > 0;
}

function durationFieldToneClass(classPrefix, value) {
  return `${classPrefix}__duration-inputs__field--${durationUnitActive(value) ? 'filled' : 'zero'}`;
}

function durationSepToneClass(classPrefix, value) {
  return `${classPrefix}__duration-inputs__sep--${durationUnitActive(value) ? 'filled' : 'zero'}`;
}

function digitsOnly(raw) {
  return raw.replace(/\D/g, '');
}

/** Min width floor for empty placeholder; max digit cap comes from CSS max-width on field classes. */
function durationInputMinWidthCh(value, maxDigits) {
  const len = value === '' ? 0 : String(value).length;
  const ch = Math.min(maxDigits, Math.max(1, len || 1));
  return `${ch}ch`;
}

function selectInputValue(e) {
  const el = e.target;
  if (el && typeof el.select === 'function') el.select();
}

/** Clicking the unit (not the input) when already focused: select all. */
function handleUnitMouseDown(e) {
  if (e.target.tagName === 'INPUT') return;
  const input = e.currentTarget.querySelector('input');
  if (!input) return;
  requestAnimationFrame(() => {
    input.focus();
    input.select();
  });
}

export function EstimatedDurationInputs({
  task,
  minutes: minutesOverride,
  readOnly = false,
  readOnlyText,
  readOnlyTitle,
  classPrefix,
  onEstimatedDurationMinutesChange,
  inputClassName,
}) {
  const minutes = minutesOverride ?? getEffectiveDurationMinutes(task);
  const split = useMemo(() => splitMinutesToDHM(minutes), [minutes]);

  const [d, setD] = useState(split.d);
  const [h, setH] = useState(split.h);
  const [m, setM] = useState(split.m);
  /** When all units are empty: show full d/h/m row after click/keyboard until blur leaves the control. */
  const [revealEmptyFields, setRevealEmptyFields] = useState(false);
  /**
   * True while caret is in any d/h/m input.
   * That is the only exception to requiring row hover to see empty -days/-hrs/-min slots.
   */
  const [anyDurationInputFocused, setAnyDurationInputFocused] = useState(false);
  const inputDRef = useRef(null);
  const rootRef = useRef(null);
  const fieldsRef = useRef(null);

  useEffect(() => {
    const s = splitMinutesToDHM(minutes);
    setD(s.d);
    setH(s.h);
    setM(s.m);
  }, [task.id, minutes]);

  useEffect(() => {
    setRevealEmptyFields(false);
    setAnyDurationInputFocused(false);
  }, [task.id]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const syncInputFocus = () => {
      const ae = document.activeElement;
      const inFields =
        ae?.tagName === 'INPUT' && fieldsRef.current?.contains(ae);
      setAnyDurationInputFocused(!!inFields);
    };
    const onFocusIn = (e) => {
      if (
        e.target?.tagName === 'INPUT' &&
        fieldsRef.current?.contains(e.target)
      ) {
        setAnyDurationInputFocused(true);
      }
    };
    const onFocusOut = () => {
      queueMicrotask(syncInputFocus);
    };
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('focusout', onFocusOut);
    return () => {
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  const allEmpty =
    !durationUnitActive(d) && !durationUnitActive(h) && !durationUnitActive(m);

  useEffect(() => {
    if (!allEmpty) setRevealEmptyFields(false);
  }, [allEmpty]);

  const activatePlaceholder = useCallback(() => {
    flushSync(() => setRevealEmptyFields(true));
    const el = inputDRef.current;
    if (el && typeof el.focus === 'function') {
      el.focus();
      if (typeof el.select === 'function') el.select();
    }
  }, []);

  const handleRootBlur = useCallback(
    (e) => {
      if (!allEmpty) return;
      const next = e.relatedTarget;
      if (next && rootRef.current?.contains(next)) return;
      window.setTimeout(() => {
        if (!rootRef.current?.contains(document.activeElement)) {
          setRevealEmptyFields(false);
        }
      }, 0);
    },
    [allEmpty]
  );

  const commit = useCallback(() => {
    const dn = parseInt(String(d), 10);
    const hn = parseInt(String(h), 10);
    const mn = parseInt(String(m), 10);
    const safeD = Number.isFinite(dn) && dn >= 0 ? dn : 0;
    const safeH = Number.isFinite(hn) && hn >= 0 ? Math.min(23, hn) : 0;
    const safeM = Number.isFinite(mn) && mn >= 0 ? Math.min(59, mn) : 0;
    const total = safeD * 1440 + safeH * 60 + safeM;
    if (total === 0) {
      onEstimatedDurationMinutesChange(task.id, null);
      return;
    }
    onEstimatedDurationMinutesChange(task.id, total);
  }, [d, h, m, task.id, onEstimatedDurationMinutesChange]);

  const display = minutes ? formatDurationMinutes(minutes) : null;
  const inputBase = inputClassName
    ? `${inputClassName} ${classPrefix}__duration-inputs__field`
    : `${classPrefix}__date-row-number-input ${classPrefix}__duration-inputs__field`;

  const uid = useId();
  const idD = `${uid}-d`;
  const idH = `${uid}-h`;
  const idM = `${uid}-m`;
  const unitClass = `${classPrefix}__duration-inputs__unit`;
  const minsLabel =
    durationUnitActive(m) && parseInt(String(m), 10) === 1 ? 'min' : 'mins';

  const rootClass = [
    `${classPrefix}__duration-inputs`,
    readOnly ? `${classPrefix}__duration-inputs--read-only` : '',
    allEmpty ? `${classPrefix}__duration-inputs--all-empty` : '',
    allEmpty && revealEmptyFields ? `${classPrefix}__duration-inputs--all-empty-reveal` : '',
    anyDurationInputFocused ? `${classPrefix}__duration-inputs--any-input-focused` : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (readOnly) {
    const shown = readOnlyText || display || '—';
    return (
      <div
        ref={rootRef}
        className={rootClass}
        title={readOnlyTitle || shown}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span className={`${classPrefix}__duration-inputs__readonly-value`}>
          {shown}
        </span>
      </div>
    );
  }

  const unitClassFor = (value) =>
    `${unitClass}${!durationUnitActive(value) ? ` ${classPrefix}__duration-inputs__unit--zero` : ''}`;

  return (
    <div
      ref={rootRef}
      className={rootClass}
      title={display || undefined}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onBlur={handleRootBlur}
    >
      {allEmpty && (
        <button
          type="button"
          className={`${classPrefix}__duration-inputs__empty-placeholder`}
          onClick={(e) => {
            e.stopPropagation();
            activatePlaceholder();
          }}
          onFocus={(e) => {
            e.stopPropagation();
            activatePlaceholder();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              activatePlaceholder();
            }
          }}
          title="Add estimated time"
        >
          —
        </button>
      )}
      <div
        ref={fieldsRef}
        className={`${classPrefix}__duration-inputs__fields`}
      >
      <label className={unitClassFor(d)} htmlFor={idD} onMouseDown={handleUnitMouseDown}>
        <input
          ref={inputDRef}
          id={idD}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          className={`${inputBase} ${classPrefix}__duration-inputs__field--d ${durationFieldToneClass(classPrefix, d)}`}
          style={{
            minWidth: durationInputMinWidthCh(d, 4),
          }}
          value={d}
          onChange={(e) => setD(digitsOnly(e.target.value))}
          onFocus={selectInputValue}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
          placeholder="-"
          title="Days"
        />
        <span className={`${classPrefix}__duration-inputs__sep ${durationSepToneClass(classPrefix, d)}`}>days</span>
      </label>
      <label className={unitClassFor(h)} htmlFor={idH} onMouseDown={handleUnitMouseDown}>
        <input
          id={idH}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          className={`${inputBase} ${classPrefix}__duration-inputs__field--h ${durationFieldToneClass(classPrefix, h)}`}
          style={{
            minWidth: durationInputMinWidthCh(h, 2),
          }}
          value={h}
          onChange={(e) => setH(digitsOnly(e.target.value))}
          onFocus={selectInputValue}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
          placeholder="-"
          title="Hours"
        />
        <span className={`${classPrefix}__duration-inputs__sep ${durationSepToneClass(classPrefix, h)}`}>hrs</span>
      </label>
      <label className={unitClassFor(m)} htmlFor={idM} onMouseDown={handleUnitMouseDown}>
        <input
          id={idM}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          className={`${inputBase} ${classPrefix}__duration-inputs__field--m ${durationFieldToneClass(classPrefix, m)}`}
          style={{
            minWidth: durationInputMinWidthCh(m, 2),
          }}
          value={m}
          onChange={(e) => setM(digitsOnly(e.target.value))}
          onFocus={selectInputValue}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
          placeholder="-"
          title="Minutes"
        />
        <span className={`${classPrefix}__duration-inputs__sep ${durationSepToneClass(classPrefix, m)}`}>{minsLabel}</span>
      </label>
      </div>
    </div>
  );
}
